import React, { useState } from "react";
import { useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { communityServicesAPI } from "../../../services/api";

const UpdateAttendanceModal = ({ isOpen, onClose, programId, volunteers = [], onUpdate }) => {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (isOpen) {
        document.body.style.overflow = "hidden";
        } else {
        document.body.style.overflow = "auto";
        }

        return () => {
        document.body.style.overflow = "auto";
        };
    }, [isOpen]);


    const { data: attendanceData, isLoading: isLoadingAttendance } = useQuery(
        ["community-services", "attendances", programId],
        async () => {
            const res = await communityServicesAPI.getAttedancesById(programId);
            return res?.data?.data || res?.data;
        },
        { 
            enabled: Boolean(isOpen && programId),
            refetchInterval: isOpen ? 30000 : false,
            refetchOnWindowFocus: true,
        }
    );

    console.log("ATTENDANCE DATA COMM: ", attendanceData)

    const volunteersFromQuery = useMemo(() => {
        const raw = Array.isArray(attendanceData?.program_attendances)
            ? attendanceData.program_attendances
            : [];

        return raw.map((a, idx) => {
            const v = a?.volunteer || {};
            const id = v?.volunteer_id ?? a?.volunteer_id ?? a?.attendance_id ?? (idx + 1);
            const name = (v?.full_name ?? "").trim();
            const userId = String(v?.resident_id ?? "");
            const initials = name
                .split(" ")
                .filter(Boolean)
                .map((n) => n[0])
                .join("")
                .toUpperCase();
            const present = String(a?.status ?? "").toLowerCase() === "present";
            return { id, name, userId, initials, present };
        });
    }, [attendanceData]);

    const volunteersToShow = useMemo(() => {
        if (volunteersFromQuery.length) return volunteersFromQuery;
        return (Array.isArray(volunteers) ? volunteers : []).map((v, idx) => {
            const name = v?.name ?? v?.full_name ?? v?.volunteer?.full_name ?? "";
            const statusStr = (((v?.status ?? v?.attendance_status ?? v?.attendance?.status) ?? "") + "").toLowerCase();
            const present = Boolean(
                v?.present ??
                v?.is_present ??
                v?.attended ??
                (statusStr === "present" || statusStr === "checked-in" || statusStr === "checked_in" || statusStr === "1" || statusStr === "true" || statusStr === "yes")
            );
            const id = v?.id ?? v?.volunteer_id ?? v?.user_id ?? v?.volunteer?.volunteer_id ?? (idx + 1);
            const userId = v?.userId ?? v?.resident?.barangay_id_number ?? "";
            const initials = (v?.initials ?? name)
                .split(" ")
                .filter(Boolean)
                .map((n) => n[0])
                .join("")
                .toUpperCase();
            return { id, name, userId, initials, present };
        });
    }, [volunteersFromQuery, volunteers]);

    const [attendance, setAttendance] = useState({});

    useEffect(() => {
        if (!isOpen) return;
        const initial = volunteersToShow.reduce((acc, v) => {
            acc[v.id] = Boolean(v.present);
            return acc;
        }, {});
        setAttendance(initial);
    }, [isOpen, volunteersToShow]);

    const { mutate: updateAttendances, isLoading: isSaving } = useMutation(
        (payload) => communityServicesAPI.updateProgramAttendance(programId, payload),
        {
            onSuccess: () => {
                toast.success("Attendance updated");
                queryClient.invalidateQueries(["community-services", "attendances", programId]);
                queryClient.invalidateQueries(["community-services", "program", programId]);
                queryClient.invalidateQueries(["community-services"]);
                onUpdate?.(attendance);
                onClose();
            },
            onError: () => toast.error("Failed to update attendance"),
        }
    );

    if (!isOpen) return null;

    const handleCheckboxChange = (volunteerId) => {
        setAttendance((prev) => ({
            ...prev,
            [volunteerId]: !prev[volunteerId],
        }));
    };


    const handleUpdate = () => {
        if (!programId) {
            toast.error("Missing program ID");
            return;
        }
        const payload = {
            attendances: volunteersToShow.map((v) => ({
                volunteer_id: v.id,
                status: Boolean(attendance[v.id]) ? "present" : "absent",
            })),
        };
        updateAttendances(payload);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Update Attendance
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-6"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    {/* Instruction Text */}
                    <p className="text-sm text-gray-600 mb-6">
                        Mark the volunteers who were present at the program.
                    </p>

                    {/* Volunteers List */}
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {isLoadingAttendance ? (
                            <div className="text-sm text-gray-500">Loading attendances...</div>
                        ) : volunteersToShow.length === 0 ? (
                            <div className="text-sm text-gray-500">No volunteers found</div>
                        ) : (
                        volunteersToShow.map((volunteer) => {
                            const isPresent = Boolean(attendance[volunteer.id] ?? volunteer.present ?? false);
                            
                            return (
                                <div
                                    key={volunteer.id}
                                    className="border border-gray-200 rounded-lg p-4"
                                >
                                    <div className="flex items-center gap-3">
                                        {/* Checkbox */}
                                        <input
                                            type="checkbox"
                                            checked={isPresent}
                                            onChange={() =>
                                                handleCheckboxChange(volunteer.id)
                                            }
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                        />

                                        {/* User In */}
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-xs font-medium text-white">
                                                {volunteer.initials}
                                            </div>
                                        </div>

                                        {/* Volunteer Details */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 mb-1">
                                                {volunteer.name}
                                            </p>
                                            <div className="flex items-center gap-2 text-xs">
                                                <span className={`px-3 py-1 rounded-full ${
                                                    isPresent 
                                                        ? 'bg-blue-100 text-blue-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {isPresent ? "Present" : "Absent"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }))}
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200"></div>

                {/* Footer */}
                <div className="p-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpdate}
                        disabled={isSaving}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
                    >
                        {isSaving ? "Saving..." : "Update Attendance"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateAttendanceModal;